
import {ApptProps} from 'components/ApptDetails2';
import {RenewInterval} from 'components/checkout/query/types';
import {ObjAppt} from 'components/checkout/query/types.api-appt';
import moment from 'moment-timezone';
import {State} from 'templates/booking/Booking/reducer';

type Data = State['result']['data'];

export type Parsed = {
	appt: ApptProps

	credits: {
		count: number
		text: string
	}

	membership: {
		count: number
		interval?: RenewInterval
		text: string
		renewsOn: string
	}
};

const parseCredits = (data: Data, appt: ApptProps): Parsed['credits'] => {
	const credits = data?.credits.user?.credits.find(
		(c) => c.product.id === appt.productID
	);

	// None found, return count & text of 0
	if (!credits) {
		return {
			count: 0,
			text: `0 ${appt.product} credits`,
		};
	}

	return {
		count: credits.count,
		text: [
			`${credits.count}`,
			appt.product,
			credits.count === 1 ? 'credit' : 'credits',
		].join(' ')
	};
};

const parseMemberships = (data: Data, appt: ApptProps): Parsed['membership'] => {
	const membership = data?.credits.user?.memberships.find((m) => (
		m.product.id === appt.productID &&
		Boolean(m.renewsOn) &&
		Boolean(m.upcoming)
	));

	if (!membership) {
		return {
			count: 0,
			text: `0 ${appt.product} credits`,
			renewsOn: '',
		};
	}

	return {
		count: membership.upcoming.price.quantity,
		interval: membership.upcoming.renewInterval,
		text: [
			`${membership.upcoming.price.quantity}`,
			appt.product,
			membership.upcoming.price.quantity === 1 ? 'credit' : 'credits',
		].join(' '),
		renewsOn: moment(membership.renewsOn.date).format('MMM D, YYYY'),
	};
};

export const parseResult = (state: State): Parsed | Record<string, never> => {
	const {data} = state.result;
	if (!data) {
		return {};
	}

	// Verify we have a valid appt in our data
	const appt = new ObjAppt(data.appt.info).props();
	if (!appt) {
		return {};
	}

	// Format results to be usable for components
	return {
		appt,
		credits: parseCredits(data, appt),
		membership: parseMemberships(data, appt),
	};
};
